import { DropdownDownFill18Icon } from "@vygruppen/spor-icon-react";
import DashedLine from "features/CenterContent/VehicleDetails/shared/DashedLine";
import { ReactNode, useCallback, useState } from "react";
import { tokens } from "shared/components/Spor";
import { FCWC } from "shared/utils/FCWC";
import styled, { css } from "styled-components";

const subgridStyle = css`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

const ExpandableWrapper = styled.div<{
  $asSubgrid: boolean;
  $isExpanded: boolean;
}>`
  ${({ $asSubgrid, $isExpanded }) =>
    $asSubgrid
      ? css`
          ${subgridStyle}

          grid-template-rows: auto ${$isExpanded ? "2px 1fr" : "0 0"};
          transition-property: grid-template-rows;
          overflow: hidden;

          > .dashedLine {
            grid-column: 1 / -1;
          }
        `
      : css`
          width: 100%;

          height: max-content;
          transition-property: height;
        `}

  transition-duration: ${tokens.default.time.transition.fast.duration};
  transition-timing-function: ${tokens.default.time.transition.fast.timing};
`;

const ExpandableHeaderWrapper = styled.button<{
  $asSubgrid: boolean;
}>`
  border-radius: ${tokens.default.size.spacing[1]};
  padding: ${tokens.default.size.spacing[2.5]};
  background-color: ${({ theme }) => theme.colorBackgroundTertiary};
  gap: ${tokens.default.size.spacing[2]};
  position: relative;

  ${({ $asSubgrid }) =>
    $asSubgrid
      ? css`
          ${subgridStyle}

          justify-items: start;
          align-items: center;

          grid-row: 1;
        `
      : ""}
`;

const ExpandableBodyWrapper = styled.div<{
  $asSubgrid: boolean;
  $isExpanded: boolean;
}>`
  overflow: hidden;
  transition-property: height;

  border-radius: ${tokens.default.size.spacing[1]};
  padding: ${tokens.default.size.spacing[2]};
  background-color: ${({ theme }) => theme.colorBackgroundTertiary};
  gap: ${tokens.default.size.spacing[2]};

  ${({ $isExpanded }) =>
    $isExpanded
      ? css`
          height: max-content;
          transition-delay: ${tokens.default.time.transition.fast.duration};
        `
      : css`
          height: 0;
          transition-delay: ${tokens.default.time.transition.fast.duration};
        `}

  ${({ $asSubgrid }) =>
    $asSubgrid
      ? css`
          ${subgridStyle}

          grid-row: 3;
        `
      : ""};
`;

type ExpandableProps = {
  asSubgrid?: boolean;
  defaultExpanded?: boolean;
  headerContent?: ReactNode;
};

export const Expandable: FCWC<ExpandableProps> = ({
  asSubgrid = false,
  defaultExpanded = false,
  headerContent,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggleExpanded = useCallback(
    () => setExpanded((prev) => !prev),
    [setExpanded],
  );

  return (
    <ExpandableWrapper $asSubgrid={asSubgrid} $isExpanded={expanded}>
      <ExpandableHeaderWrapper $asSubgrid={asSubgrid} onClick={toggleExpanded}>
        {headerContent}
        <DropdownDownFill18Icon
          style={{
            position: "absolute",
            right: tokens.default.size.spacing[2],
            top: "calc((100% - 18px) / 2)",
          }}
        />
      </ExpandableHeaderWrapper>
      <DashedLine variant="colorBackgroundTertiary" strokeDasharray="3 6" />
      <ExpandableBodyWrapper $asSubgrid={asSubgrid} $isExpanded={expanded}>
        {children}
      </ExpandableBodyWrapper>
    </ExpandableWrapper>
  );
};
